<template>
  <div class="home">
    <img
      alt="Recognzye.AI logo"
      class="home-logo"
      :src="logoSrc"
    >
    <h1>{{ $t('home.welcome', { service: serviceLabel }) }}</h1>
    <!-- 
    <p>Auth: {{ isAuthenticated }}</p>
    <p>Has access to service: {{ hasAccessToService }}</p> 
    -->
    <template v-if="isAuthenticated">
      <router-link
        v-if="!hasAccessToService"
        to="/login"
        class="login-link"
      >
        <button class="enter-btn">
          {{ $t('login.subscribeButton', { type: serviceLabel }) }}
        </button>
      </router-link>
    </template>
    <template v-else> 
      <router-link
        to="/login"
        class="login-link"
      >
        <button class="enter-btn">
          {{ $t('home.enter') }}
        </button>
      </router-link>
    </template>

    <template
      v-for="boxes in rows"
      :key="boxes.title"
    >
      <div
        class="boxes"
        :class="{ 'single-box': hasSingleBox }"
        v-if="boxes.length > 0"
      >
        <template
          v-for="box in boxes"
          :key="box.title"
        >
          <div
            :class="box.css_class"
            v-if="box.condition === null || box.condition() === true"
          >
            <router-link :to="box.link">
              <h2 class="title">
                {{ t(box.title) }}
              </h2>
              <p class="description">
                {{ t(box.description) }}
              </p>
              <object
                class="image"
                :data="box.svg"
                type="image/svg+xml"
                v-if="box.svg"
              />
              <img
                class="image"
                :src="box.image"
                v-else-if="box.image"
              >
              <i
                class="icon"
                :class="normalizeIconClass(box.icon)"
                v-else-if="box.icon"
              />
            </router-link>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { defineProps } from 'vue';
import { logo, logoDark, localStorageManager } from '@r7e/shared'; 

const props = defineProps({
  r7eApp: {
    type: Object,
    required: true
  },
  service: {
    type: String,
    required: true
  }
});

const theme = ref(localStorageManager.getItem('theme') || 'light');
const logoSrc = computed(() => theme.value === 'dark' ? logoDark : logo);

const { t } = useI18n(); // eslint-disable-line no-unused-vars
const isAuthenticated = ref(props.r7eApp.isAuthenticated());
const hasAccessToService = ref(props.r7eApp.hasAccessToService(props.service));
const serviceLabel = ref(t(`account_type.${props.service}`));
const rows = ref([]);
// const boxes = ref([]);

// Count visible boxes (those that pass their condition check)
const visibleBoxes = computed(() => {
  const visibleBoxesList = [];
  for (const row of rows.value) {
    for (const box of row) {
      if (box.condition === null || box.condition() === true) {
        visibleBoxesList.push(box);
      }
    }
  }
  return visibleBoxesList;
});

// Check if there's only one visible box
const hasSingleBox = computed(() => visibleBoxes.value.length === 1);

// Register observer for authentication changes
props.r7eApp.registerCredentialsObserver(() => {
  isAuthenticated.value = props.r7eApp.isAuthenticated();
  hasAccessToService.value = props.r7eApp.hasAccessToService(props.service);
});

onMounted(() => {
  // Update authentication status on mount
  isAuthenticated.value = props.r7eApp.isAuthenticated();
  hasAccessToService.value = props.r7eApp.hasAccessToService(props.service);
  const homeItems = props.r7eApp.getHomeItems();
  const rowsList = [];
  if (homeItems.length > 0)
   if (Array.isArray(homeItems[0])) {
    for (const row of homeItems) {
      rowsList.push(row.map(item => ({
        title: item.title,
        description: item.description,
        icon: item.icon || null,
        image: item.image || null,
        svg: item.svg || null,
        condition: item.condition || null,
        link: item.link,
        css_class: 'box ' + (item.css_class || 'box-default')
      })));
    }
  } else {
    rowsList.push(homeItems.map(item => ({
      title: item.title,
      description: item.description,
      icon: item.icon || null,
      image: item.image || null,
      svg: item.svg || null,
      condition: item.condition || null,
      link: item.link,
      css_class: 'box ' + (item.css_class || 'box-default')
    })));
  }

  // boxes.value = rows;
  rows.value = rowsList;

  // Initialize and listen for theme changes
  const saved = localStorageManager.getItem('theme');
  theme.value = saved === 'dark' ? 'dark' : 'light';
  localStorageManager.addEventListener(handleStorageChange);
});

onUnmounted(() => {
  localStorageManager.removeEventListener(handleStorageChange);
});

function handleStorageChange(event) {
  if (event.key === 'theme') {
    theme.value = event.newValue === 'dark' ? 'dark' : 'light';
  }
}

// Normalize Font Awesome class names between v6 (fa-solid/fa-regular/fa-brands)
// and v5 (fas/far/fab) so provided icon strings render correctly regardless of version
function normalizeIconClass(icon) {
  if (!icon) return null;
  if (Array.isArray(icon)) {
    return icon
      .map(cls => cls
        .replace(/\bfa-solid\b/g, 'fas')
        .replace(/\bfa-regular\b/g, 'far')
        .replace(/\bfa-brands\b/g, 'fab'));
  }
  if (typeof icon === 'string') {
    return icon
      .replace(/\bfa-solid\b/g, 'fas')
      .replace(/\bfa-regular\b/g, 'far')
      .replace(/\bfa-brands\b/g, 'fab');
  }
  return icon;
}
</script>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* min-height: 100vh; */
  min-height: 60vh;
}
.home-logo {
  width: max(6%, 150px);
  transition: transform 0.3s ease;
}
.home-logo:hover {
  transform: scale(1.1);
}

.enter-link,
.login-link {
  /* margin-top: 10%; */
  text-decoration: none;
}

.enter-btn {
  font-size: 2rem;
  padding: 1rem 3rem;
  border-radius: 8px;
  background-color: #42b983;
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: background 0.2s;
}
.enter-btn:hover {
  background-color: #369870;
}
div.boxes {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 1rem;
  /* width: 100%; */
  /* width: 100%;
  max-width: 1200px; */
  margin: 0 auto;
  padding: 1rem;
  /* border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1); */
}
div.boxes.single-box {
  justify-content: center;
}
div.boxes div.box {
  background-color: var(--color-frame-bg);
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex: 1 0 calc((100% - 2rem) / 3);
  width: 100%;
  max-width: calc((100% - 2rem) / 3);
  min-width: 200px;
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--color-text-dark);
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}
div.boxes div.box h2 {
  color: var(--color-text-dark);
}
div.boxes div.box a {
  text-decoration: none;
}
div.boxes div.box:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  background-color: var(--color-frame-bg-hover); /* subtle tint on hover */
}
div.boxes div.box .icon {
  font-size: 3rem;
  color: var(--color-border-dark);
  opacity: 0.6;
}
div.boxes div.box .image {
  /* width: 100%; */
  height: 100%;
  max-width: 80px;
  max-height: 80px;
  object-fit: contain;
}

div.box p.description {
  font-size: 1rem;
  font-style: italic;
  min-height: calc(1em * 1.2 * 2); /* 3 lines at 1em font-size, 1.2 line-height */
  display: flex;
  align-items: top;
  justify-content: center;
}
</style>
